import { useEffect, useRef, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import ObjectID from "bson-objectid";
import {
  updateProgressCommentsUsed,
  updateProgressNotesUsed,
} from "../../actions/progress";
import { addComment } from "../../actions/comment";
import {
  SIGNAL_START_STOPWATCH,
  ADD_NEW_COMMENT,
} from "../../constants/actionConstants";
import { COMMENT_EXPLAIN } from "../../constants/commentsConstants";
import { retrieve_nearby_text_content } from "../Global/CommentUtils";
import { useLearnCtx } from "./LearnPage";
import { useHint } from "../../hooks/useHint";
import HintBubble from "./HintBubble";
import TitleComp from "./content/TitleComp";
import ParagraphComp from "./content/ParagraphComp";
import EquationComp from "./content/EquationComp";
import FigureComp from "./content/FigureComp";
import MultiFigureComp from "./content/MultiFigureComp";
import ProblemSetComp from "./content/ProblemSetComp";
import AttributionComp from "./content/AttributionComp";
import FeedbackComp from "./content/FeedbackComp";
import IntFigureComp from "./content/IntFigureComp/IntFigureComp";
import IntFigureDiscreteComp from "./content/IntFigureComp/IntFigureDiscreteComp";
import ListComp from "./content/ListComp";
import QuestionDialog from "./QuestionDialog";
import NotesPanel from "./NotesPanel";
import EqTooltipPanel from "./EqTooltipPanel";

/** Dispatch correct content component for a content item type. */
function ContentItem({
  item,
  sectionId,
  onTooltipOpen,
  tooltipContentId,
  firstTooltipEqId,
  panelOpen,
  onExplain,
  onAskAI,
}) {
  switch (item.type) {
    case "EDCOMP_TITLE":
      return <TitleComp item={item} />;
    case "EDCOMP_PARAGRAPH":
      return (
        <ParagraphComp item={item} onExplain={onExplain} onAskAI={onAskAI} />
      );
    case "EDCOMP_EQUATION":
    case "EDCOMP_EQN_MULTILINE":
      return (
        <EquationComp
          item={item}
          onTooltipOpen={onTooltipOpen}
          tooltipOpen={tooltipContentId === item._id}
          showHint={item._id === firstTooltipEqId}
          panelOpen={panelOpen}
          onExplain={onExplain}
          onAskAI={onAskAI}
        />
      );
    case "EDCOMP_FIGURE":
      return <FigureComp item={item} />;
    case "EDCOMP_MULTIFIGURE":
      return <MultiFigureComp item={item} />;
    case "EDCOMP_PROBLEM_SET":
      return <ProblemSetComp item={item} />;
    case "EDCOMP_ATTRIBUTION":
      return <AttributionComp item={item} />;
    case "EDCOMP_FEEDBACK":
      return <FeedbackComp item={item} sectionId={sectionId} />;
    case "EDCOMP_INT_FIGURE":
      return <IntFigureComp item={item} />;
    case "EDCOMP_INT_FIGURE_DISCRETE":
      return <IntFigureDiscreteComp item={item} />;
    case "EDCOMP_LIST":
      return <ListComp item={item} onExplain={onExplain} onAskAI={onAskAI} />;
    default:
      return null;
  }
}

/**
 * Wraps a single frame with its sidebar icons and slide-in panels.
 * @param {{ frame: object }} props
 */
export default function FrameWrapper({
  frame,
  sectionId,
  sectionIndex,
  isFirst = false,
  isLast,
  onFinished,
}) {
  const {
    setActiveFrameId,
    openFramePanel,
    openPanel,
    closeFramePanel,
    openNotesPanels,
    openNotes,
    closeNotes,
    commentsMap,
    canvasRef,
    openProblemSetId,
    openComments,
  } = useLearnCtx();

  const dispatch = useDispatch();
  const { id: courseId } = useParams();
  const commentsUsed = useSelector(
    (state) => state.ProgressReducer.commentsUsed,
  );
  const notesUsed = useSelector((state) => state.ProgressReducer.notesUsed);
  const content = useSelector((state) => state.ContentReducer.content);
  const courseTitle = useSelector(
    (state) => state.CourseStructureReducer.courseTitle,
  );

  const { visible: hintQVisible, dismiss: dismissHintQ } = useHint(
    "frame-q-icon",
    {
      enabled: isFirst,
      value: commentsUsed,
      onDismiss: () => dispatch(updateProgressCommentsUsed(courseId)),
    },
  );
  const { visible: hintNotesVisible, dismiss: dismissHintNotes } = useHint(
    "frame-notes-icon",
    {
      enabled: isFirst,
      value: notesUsed,
      onDismiss: () => dispatch(updateProgressNotesUsed(courseId)),
    },
  );

  // ID of the first equation-with-tooltip in this frame; null for non-first frames.
  const firstTooltipEqId = isFirst
    ? (frame.contents.find(
        (c) =>
          (c.type === "EDCOMP_EQUATION" || c.type === "EDCOMP_EQN_MULTILINE") &&
          c.data?.tooltip_elements?.length > 0,
      )?._id ?? null)
    : null;

  const frameId = frame._id;
  const wrapperRef = useRef(null);
  // Vertical offset (px) of the equation panel relative to this frame's top edge.
  // Updated each time an equation tooltip icon is clicked.
  const [panelOffset, setPanelOffset] = useState(12);
  // Measured height of this frame (px) — drives the side-panel max-height cap.
  const [frameHeight, setFrameHeight] = useState(null);
  // Text captured from a selection when the user clicks "Ask AI" in the toolbar.
  const [pendingHighlight, setPendingHighlight] = useState(null);

  /* ── Which frame panel is open for this frame ── */
  // Notes can be open on multiple frames at once; Q/Eq remain exclusive (openFramePanel).
  const notesAll = useSelector((state) => state.ProgressReducer.notes);
  const notesOpen = openNotesPanels.has(frameId);
  const hasNote = notesAll.some(
    (n) => String(n.frameId) === String(frameId) && !!n.note?.trim(),
  );
  const activePanelType = notesOpen
    ? "notes"
    : openFramePanel?.frameId === frameId
      ? openFramePanel.type
      : null;
  const activeTooltipId =
    activePanelType === "equation" ? openFramePanel?.contentId : null;

  /* ── Find equation items (for tooltip panel) ── */
  const equationItems = frame.contents.filter(
    (c) => c.type === "EDCOMP_EQUATION" || c.type === "EDCOMP_EQN_MULTILINE",
  );
  const tooltipItem = activeTooltipId
    ? (equationItems.find((e) => e._id === activeTooltipId) ?? equationItems[0])
    : null;

  /* ── Problem-set-only frame ── */
  const problemSetOnlyItem =
    // frame.contents.length === 1 &&
    frame.contents[0].type === "EDCOMP_PROBLEM_SET" ? frame.contents[0] : null;
  const problemSetOpen = problemSetOnlyItem
    ? openProblemSetId === problemSetOnlyItem._id
    : true; // non-PS frames always show icons

  /* ── Attribution-first frame: suppress gutter icons ── */
  const isAttributionFirst = frame.contents[0]?.type === "EDCOMP_ATTRIBUTION";

  /* ── Title-first frame with space: offset icons to match title top padding ── */
  const firstItem = frame.contents[0];
  const titleSpaceOffset =
    firstItem?.type === "EDCOMP_TITLE" && firstItem.data?.space
      ? firstItem.data.space
      : null;

  /* ── Comment count ── */
  const commentCount = (commentsMap[frameId] ?? []).filter(
    (c) => !c.deleted,
  ).length;

  /* ── IntersectionObserver: update context active frame ── */
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !wrapperRef.current) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setActiveFrameId(frameId);
      },
      { root: canvas, threshold: 0.5 },
    );

    observer.observe(wrapperRef.current);
    return () => observer.disconnect();
  }, [frameId, canvasRef, setActiveFrameId]);

  /* ── ResizeObserver: keep frameHeight in sync ── */
  useEffect(() => {
    const el = wrapperRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      setFrameHeight(Math.round(entries[0].contentRect.height));
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  /* ── Close panels when a problem-set-only frame's set closes ── */
  useEffect(() => {
    if (!problemSetOnlyItem) return;
    if (!problemSetOpen) {
      if (openFramePanel?.frameId === frameId) closeFramePanel();
      if (openNotesPanels.has(frameId)) closeNotes(frameId);
    }
  }, [problemSetOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ── Clear pending highlight whenever the question panel closes ── */
  useEffect(() => {
    if (activePanelType !== "question") setPendingHighlight(null);
  }, [activePanelType]);

  /* ── Explain handler: builds a COMMENT_EXPLAIN and opens the thread ── */
  const submitExplain = useCallback(
    (text) => {
      let edited_text = text.replaceAll("<br>", "").replace(/\s+$/g, "");

      const frame_text = retrieve_nearby_text_content(content, frameId);
      dispatch({ type: SIGNAL_START_STOPWATCH, payload: Math.random() });

      const date_object = new Date();
      const user = JSON.parse(localStorage.getItem("profile"));

      let title_text, comment_text;

      if (edited_text.startsWith("Equation:")) {
        edited_text = edited_text.replace("Equation:", "");
        const next_colon = edited_text.indexOf(":");
        const eq_number =
          next_colon === 0 ? "" : edited_text.substring(0, next_colon);
        edited_text = edited_text.slice(next_colon + 1);
        title_text =
          eq_number === ""
            ? "Explain this equation"
            : "Explain equation " + eq_number;
        title_text =
          title_text.length < 50
            ? title_text
            : title_text.substring(0, 47) + "...";
        comment_text =
          "Can you explain this equation in simple terms?$$" +
          edited_text +
          "$$";
      } else {
        title_text =
          edited_text.length < 50
            ? "Explain: " + edited_text.replaceAll("$", "")
            : "Explain: " +
              edited_text.substring(0, 47).replaceAll("$", "") +
              "...";
        comment_text =
          "Can you explain what is meant by: '" + edited_text + "'?";
      }

      const reply = {
        author: "AI",
        author_id: user.result._id,
        date: date_object.getTime(),
        _id: ObjectID().toHexString(),
        upvotes: 0,
        contents: "AI response pending",
        children: [],
      };

      const newComment = {
        author: user.result.name,
        author_id: user.result._id,
        date: date_object.getTime(),
        _id: ObjectID().toHexString(),
        upvotes: 0,
        type: COMMENT_EXPLAIN,
        title: title_text,
        contents: comment_text,
        children: [reply],
        content_item_id: frameId,
        deleted: false,
        contentId: courseId,
        courseTitle,
      };

      dispatch({
        type: ADD_NEW_COMMENT,
        payload: { sectionIndex, newComment },
      });
      openComments(frameId, newComment._id);
      dispatch(
        addComment(courseId, sectionIndex, sectionId, newComment, frame_text),
      );
    },
    [
      content,
      frameId,
      courseId,
      sectionId,
      sectionIndex,
      courseTitle,
      dispatch,
      openComments,
    ],
  );

  /* ── Handlers ── */
  const handleTooltipOpen = (contentId, e) => {
    if (activePanelType === "equation" && activeTooltipId === contentId) {
      closeFramePanel();
    } else {
      // Align the panel's top edge with the triggering icon's top edge
      if (e?.currentTarget && wrapperRef.current) {
        const iconTop = e.currentTarget.getBoundingClientRect().top;
        const frameTop = wrapperRef.current.getBoundingClientRect().top;
        setPanelOffset(Math.max(0, iconTop - frameTop));
      }
      openPanel(frameId, "equation", contentId);
    }
  };

  const handleQOpen = () => {
    dismissHintQ();
    if (activePanelType === "question") closeFramePanel();
    else openPanel(frameId, "question");
  };

  const handleAskAI = useCallback(
    (text) => {
      setPendingHighlight(text);
      if (activePanelType !== "question") openPanel(frameId, "question");
    },
    [activePanelType, frameId, openPanel],
  );

  const handleNotesOpen = () => {
    dismissHintNotes();
    openNotes(frameId);
  };

  return (
    <>
      <div className="frame__extension_left"></div>
      <section
        ref={wrapperRef}
        data-frame-id={frameId}
        className={`frame${activePanelType ? " frame--panel-open" : ""}${activePanelType === "notes" || activePanelType === "question" ? " frame--highlighted" : ""}`}
      >
        {/* Content items */}
        <div className="frame__content">
          {frame.contents.map((item, i) => (
            <ContentItem
              key={item._id ?? i}
              item={item}
              sectionId={sectionId}
              onTooltipOpen={handleTooltipOpen}
              tooltipContentId={activeTooltipId}
              firstTooltipEqId={firstTooltipEqId}
              panelOpen={!!activePanelType}
              onExplain={submitExplain}
              onAskAI={handleAskAI}
            />
          ))}
          {isLast && (
            <div className="canvas__finished-wrap">
              <button className="canvas__finished-btn" onClick={onFinished}>
                I'M FINISHED!
              </button>
            </div>
          )}
        </div>

        {/* Panel — absolutely positioned, out-of-flow, scrolls with the frame */}
        {activePanelType && (
          <div
            className="frame__panel"
            style={{
              // On narrow screens the panel is a fixed bottom sheet; skip the inline top
              // so CSS `bottom: 0` can anchor it without fighting an inline `top` value.
              ...(!window.matchMedia("(max-width: 1023px)").matches &&
                (activePanelType === "equation"
                  ? { top: panelOffset }
                  : titleSpaceOffset
                    ? { top: `calc(12px + ${titleSpaceOffset})` }
                    : {})),
              // Expose the frame's live height so CSS can cap the panel via min().
              // --panel-top mirrors the CSS top value so the cap is always tight.
              ...(frameHeight !== null
                ? { "--frame-h": `${frameHeight}px` }
                : {}),
              "--panel-top":
                activePanelType === "equation"
                  ? `${panelOffset}px`
                  : titleSpaceOffset
                    ? `calc(12px + ${titleSpaceOffset})`
                    : "12px",
            }}
          >
            {activePanelType === "question" && (
              <QuestionDialog
                frameId={frameId}
                sectionId={sectionId}
                sectionIndex={sectionIndex}
                onClose={closeFramePanel}
                highlightedInfo={pendingHighlight}
              />
            )}
            {activePanelType === "notes" && (
              <NotesPanel
                frameId={frameId}
                onClose={() => closeNotes(frameId)}
              />
            )}
            {activePanelType === "equation" && tooltipItem && (
              <EqTooltipPanel item={tooltipItem} onClose={closeFramePanel} />
            )}
          </div>
        )}
      </section>

      {/* Icons — grid column 2, outside the white paper */}
      <div
        className={`frame__icons${problemSetOnlyItem && !problemSetOpen ? " frame__icons--hidden" : ""}${isAttributionFirst ? " frame__icons--hidden" : ""}`}
        style={titleSpaceOffset ? { marginTop: titleSpaceOffset } : undefined}
        aria-label="Frame tools"
      >
        <div style={{ position: "relative" }}>
          <button
            className={`frame__icon ${activePanelType === "question" ? "frame__icon--active" : ""}`}
            onClick={handleQOpen}
            aria-label={`Discussion${commentCount ? ` (${commentCount})` : ""}`}
            title="Questions & comments"
          >
            <svg width="14" height="14" viewBox="0 0 18 18" fill="none">
              <path
                d="M3 3h12v9H9l-4 3V12H3V3z"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinejoin="round"
              />
            </svg>
            {commentCount > 0 && (
              <span className="frame__icon-badge">{commentCount}</span>
            )}
          </button>
          {hintQVisible && !activePanelType && (
            <HintBubble
              text="Use this widget to ask questions about the text and see other people's questions. You can also highlight the text or an equation to ask a question directly about it."
              onDismiss={dismissHintQ}
              anchor="right-from-bottom"
            />
          )}
        </div>

        <div style={{ position: "relative" }}>
          <button
            className={`frame__icon ${activePanelType === "notes" ? "frame__icon--active" : ""}`}
            onClick={handleNotesOpen}
            aria-label="Notes"
            title="Personal notes"
          >
            <svg width="14" height="14" viewBox="0 0 18 18" fill="none">
              <rect
                x="3"
                y="3"
                width="12"
                height="12"
                rx="2"
                stroke="currentColor"
                strokeWidth="1.5"
              />
              <path
                d="M6 7h6M6 10h4"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
            </svg>
            {hasNote && <span className="frame__icon-dot" aria-hidden="true" />}
          </button>
          {hintNotesVisible && !activePanelType && (
            <HintBubble
              text="Use this widget to make your own notes about this section."
              onDismiss={dismissHintNotes}
              anchor="right-from-top"
            />
          )}
        </div>
      </div>
      <div className="frame__extension_right"></div>
    </>
  );
}
